import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * OpportunityRolesSection — extracted from HandshakesPage as a
 * self-contained module. All state, handlers, and rendering for the
 * "Opportunity Roles" concept live here so it can be dropped into any
 * page (currently used by SearchesPage under the Opportunities filter).
 *
 * Handles: list load, create/edit/delete role, expand to see details,
 * search + add candidates to a role, update candidate status per role.
 *
 * No page chrome — just the section body. Wrap in your own container.
 */
export default function OpportunityRolesSection() {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRole, setNewRole] = useState({ title: '', company: '', location: '', description: '', source: '', contact_name: '', contact_email: '', url: '' });
  const [savingRole, setSavingRole] = useState(false);
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [roleCandidates, setRoleCandidates] = useState({});
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateResults, setCandidateResults] = useState([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const res = await axios.get(`${API_URL}/opportunity-roles`, { headers: authHeaders });
      setRoles(res.data);
    } catch (err) { console.error('Error loading roles:', err); }
    finally { setRolesLoading(false); }
  };

  const handleCreateRole = async () => {
    if (!newRole.title.trim()) return;
    setSavingRole(true);
    try {
      await axios.post(`${API_URL}/opportunity-roles`, newRole, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setNewRole({ title: '', company: '', location: '', description: '', source: '', contact_name: '', contact_email: '', url: '' });
      setShowAddRole(false);
      loadRoles();
    } catch (err) { alert('Failed to create role'); }
    finally { setSavingRole(false); }
  };

  const handleUpdateRole = async (id, updates) => {
    try {
      await axios.put(`${API_URL}/opportunity-roles/${id}`, updates, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      loadRoles();
      setEditingRole(null);
    } catch (err) { alert('Failed to update'); }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Delete this role and all candidate associations?')) return;
    try { await axios.delete(`${API_URL}/opportunity-roles/${id}`, { headers: authHeaders }); loadRoles(); }
    catch (err) { alert('Failed to delete'); }
  };

  const loadRoleCandidates = async (roleId) => {
    try {
      const res = await axios.get(`${API_URL}/opportunity-roles/${roleId}/candidates`, { headers: authHeaders });
      setRoleCandidates(prev => ({ ...prev, [roleId]: res.data }));
    } catch (err) { console.error(err); }
  };

  const handleExpandRole = (roleId) => {
    if (expandedRoleId === roleId) { setExpandedRoleId(null); return; }
    setExpandedRoleId(roleId);
    loadRoleCandidates(roleId);
    setCandidateSearch(''); setCandidateResults([]);
  };

  const searchCandidatesForRole = async (q) => {
    setCandidateSearch(q);
    if (q.length < 2) { setCandidateResults([]); return; }
    setSearchingCandidates(true);
    try {
      const res = await axios.get(`${API_URL}/opportunity-roles/search-candidates?q=${encodeURIComponent(q)}`, { headers: authHeaders });
      setCandidateResults(res.data);
    } catch (err) { console.error(err); }
    finally { setSearchingCandidates(false); }
  };

  const addCandidateToRole = async (roleId, candidateId) => {
    try {
      await axios.post(`${API_URL}/opportunity-roles/${roleId}/candidates`, { candidate_id: candidateId }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      loadRoleCandidates(roleId); loadRoles();
      setCandidateSearch(''); setCandidateResults([]);
    } catch (err) { alert('Failed to add candidate'); }
  };

  const removeCandidateFromRole = async (roleId, candidateId) => {
    try {
      await axios.delete(`${API_URL}/opportunity-roles/${roleId}/candidates/${candidateId}`, { headers: authHeaders });
      loadRoleCandidates(roleId); loadRoles();
    } catch (err) { alert('Failed to remove'); }
  };

  const updateCandidateStatus = async (roleId, candidateId, status) => {
    try {
      await axios.put(`${API_URL}/opportunity-roles/${roleId}/candidates/${candidateId}`, { status }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      loadRoleCandidates(roleId);
    } catch (err) { alert('Failed to update'); }
  };

  // Helpers
  const timeAgo = (d) => {
    if (!d) return '';
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    const m = Math.floor(days / 30);
    return m === 1 ? '1 month ago' : `${m} months ago`;
  };
  const statusBadge = (s) => {
    const styles = { open: 'bg-green-100 text-green-700', on_hold: 'bg-yellow-100 text-yellow-700', filled: 'bg-blue-100 text-blue-700', closed: 'bg-gray-100 text-gray-500' };
    const labels = { open: 'Open', on_hold: 'On Hold', filled: 'Filled', closed: 'Closed' };
    return <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${styles[s] || styles.open}`}>{labels[s] || s}</span>;
  };

  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <button onClick={() => { setShowAddRole(!showAddRole); setEditingRole(null); }} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add Role
        </button>
      </div>

      {/* Add / Edit form */}
      {(showAddRole || editingRole) && (
        <div className="bg-white rounded-lg shadow border p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{editingRole ? 'Edit Role' : 'Track a New Role'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={editingRole ? editingRole.title : newRole.title}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, title: e.target.value}) : setNewRole({...newRole, title: e.target.value})}
              placeholder="Role title *" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="text" value={editingRole ? editingRole.company : newRole.company}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, company: e.target.value}) : setNewRole({...newRole, company: e.target.value})}
              placeholder="Company" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="text" value={editingRole ? editingRole.location : newRole.location}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, location: e.target.value}) : setNewRole({...newRole, location: e.target.value})}
              placeholder="Location" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="text" value={editingRole ? editingRole.source : newRole.source}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, source: e.target.value}) : setNewRole({...newRole, source: e.target.value})}
              placeholder="Source (how you heard)" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="text" value={editingRole ? editingRole.contact_name : newRole.contact_name}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, contact_name: e.target.value}) : setNewRole({...newRole, contact_name: e.target.value})}
              placeholder="Contact person" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="email" value={editingRole ? editingRole.contact_email : newRole.contact_email}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, contact_email: e.target.value}) : setNewRole({...newRole, contact_email: e.target.value})}
              placeholder="Contact email" className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <input type="url" value={editingRole ? editingRole.url : newRole.url}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, url: e.target.value}) : setNewRole({...newRole, url: e.target.value})}
              placeholder="URL / Job posting link" className="col-span-2 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <textarea value={editingRole ? editingRole.description : newRole.description}
              onChange={(e) => editingRole ? setEditingRole({...editingRole, description: e.target.value}) : setNewRole({...newRole, description: e.target.value})}
              placeholder="Notes / description" rows="2" className="col-span-2 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setShowAddRole(false); setEditingRole(null); }} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            {editingRole ? (
              <button onClick={() => handleUpdateRole(editingRole.id, editingRole)} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Save Changes</button>
            ) : (
              <button onClick={handleCreateRole} disabled={savingRole || !newRole.title.trim()} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium">{savingRole ? 'Creating...' : 'Create Role'}</button>
            )}
          </div>
        </div>
      )}

      {/* Roles list */}
      {rolesLoading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : roles.length === 0 ? (
        <div className="bg-white rounded-lg shadow border text-center py-12">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <p className="text-gray-500 text-sm mb-1">No opportunity roles tracked yet</p>
          <p className="text-xs text-gray-400">Add roles you hear about in the market to track potential candidate fits.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {roles.map(role => (
            <div key={role.id} className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleExpandRole(role.id)}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${role.status === 'open' ? 'bg-green-100' : role.status === 'on_hold' ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                    <svg className={`w-5 h-5 ${role.status === 'open' ? 'text-green-600' : role.status === 'on_hold' ? 'text-yellow-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{role.title}</p>
                      {statusBadge(role.status)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {role.company && <span>{role.company}</span>}
                      {role.company && role.location && <span> · </span>}
                      {role.location && <span>{role.location}</span>}
                      <span className="ml-2">{role.candidate_count || 0} candidate{role.candidate_count !== 1 ? 's' : ''}</span>
                      <span className="ml-2">· {timeAgo(role.created_at)}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); setEditingRole(role); setShowAddRole(false); }} className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedRoleId === role.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {expandedRoleId === role.id && (
                <div className="border-t bg-gray-50 px-4 py-3">
                  {(role.description || role.source || role.contact_name || role.url) && (
                    <div className="mb-3 text-xs text-gray-600 space-y-1">
                      {role.description && <p>{role.description}</p>}
                      <div className="flex items-center gap-4 flex-wrap">
                        {role.source && <span>Source: <span className="font-medium">{role.source}</span></span>}
                        {role.contact_name && <span>Contact: <span className="font-medium">{role.contact_name}</span>{role.contact_email && <span className="text-gray-400"> ({role.contact_email})</span>}</span>}
                        {role.url && <a href={role.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View posting →</a>}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] text-gray-500">Status:</span>
                    {['open', 'on_hold', 'filled', 'closed'].map(s => (
                      <button key={s} onClick={() => handleUpdateRole(role.id, { ...role, status: s })}
                        className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${role.status === s ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                        {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </button>
                    ))}
                    <div className="flex-1" />
                    <button onClick={() => handleDeleteRole(role.id)} className="px-2 py-1 text-[10px] text-red-500 border border-red-200 rounded hover:bg-red-50">Delete</button>
                  </div>

                  <div className="mb-3">
                    <div className="relative">
                      <input type="text" value={candidateSearch} onChange={(e) => searchCandidatesForRole(e.target.value)}
                        placeholder="Search candidates to add..." className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8" />
                      {searchingCandidates && <div className="absolute right-3 top-2.5"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div></div>}
                    </div>
                    {candidateResults.length > 0 && (
                      <div className="mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {candidateResults.map(c => {
                          const alreadyAdded = (roleCandidates[role.id] || []).some(rc => rc.candidate_id === c.id);
                          return (
                            <button key={c.id} onClick={() => !alreadyAdded && addCandidateToRole(role.id, c.id)} disabled={alreadyAdded}
                              className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-blue-50 border-b last:border-0 ${alreadyAdded ? 'opacity-50' : ''}`}>
                              <div>
                                <span className="font-medium text-gray-900">{c.name}</span>
                                {c.current_title && <span className="text-gray-500 ml-1">{c.current_title}</span>}
                                {c.current_company && <span className="text-gray-400 ml-1">at {c.current_company}</span>}
                              </div>
                              {alreadyAdded ? <span className="text-[10px] text-gray-400">Added</span> : <span className="text-[10px] text-blue-600">+ Add</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Candidates ({(roleCandidates[role.id] || []).length})</p>
                    {(roleCandidates[role.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">No candidates linked yet. Search above to add.</p>
                    ) : (
                      <div className="space-y-1">
                        {(roleCandidates[role.id] || []).map(rc => (
                          <div key={rc.id} className="flex items-center justify-between px-3 py-2 bg-white rounded border">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/candidate/${rc.candidate_id}`)}>{rc.candidate_name}</span>
                              {rc.current_title && <span className="text-xs text-gray-500">{rc.current_title}{rc.current_company && ` at ${rc.current_company}`}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <select value={rc.status || 'potential'} onChange={(e) => updateCandidateStatus(role.id, rc.candidate_id, e.target.value)}
                                className="text-[10px] border rounded px-1.5 py-0.5 text-gray-600 focus:ring-1 focus:ring-blue-500">
                                <option value="potential">Potential</option>
                                <option value="contacted">Contacted</option>
                                <option value="interested">Interested</option>
                                <option value="not_interested">Not Interested</option>
                                <option value="placed">Placed</option>
                              </select>
                              <button onClick={() => removeCandidateFromRole(role.id, rc.candidate_id)} className="p-1 text-gray-400 hover:text-red-500">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
